import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SendIcon, SparklesIcon, MinimizeIcon, MessageCircleIcon } from 'lucide-react';
import knowledgeBase from '../data/knowledgeBase.json';
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'orion';
  timestamp: Date;
}
const Orion = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (isOpen && !isMinimized && messages.length === 0) {
      const greeting = knowledgeBase.greetings[Math.floor(Math.random() * knowledgeBase.greetings.length)];
      setMessages([{
        id: Date.now().toString(),
        text: greeting,
        sender: 'orion',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, isMinimized]);
  const findAnswer = (question: string): string => {
    const lowerQuestion = question.toLowerCase();
    // Check for greetings
    if (lowerQuestion.match(/\b(hola|hello|hi|buenos días|buenas tardes|buenas noches|hey)\b/)) {
      return knowledgeBase.greetings[Math.floor(Math.random() * knowledgeBase.greetings.length)];
    }
    // Check for contact info
    if (lowerQuestion.match(/\b(contacto|teléfono|email|correo|ubicación|dirección|horario)\b/)) {
      return `Puedes contactarnos en:\n\n📞 Teléfono: ${knowledgeBase.company.contact.phone}\n📧 Email: ${knowledgeBase.company.contact.email}\n📍 Ubicación: ${knowledgeBase.company.contact.location}\n\n⏰ Horarios:\n${knowledgeBase.company.contact.hours.weekdays}\n${knowledgeBase.company.contact.hours.saturday}\n${knowledgeBase.company.contact.hours.sunday}`;
    }
    // Check for company info
    if (lowerQuestion.match(/\b(quiénes son|quién|empresa|grupo reflex|sobre ustedes|filosofía)\b/)) {
      return `${knowledgeBase.company.description}\n\nNuestra filosofía:\n${knowledgeBase.company.philosophy.map(p => `• ${p}`).join('\n')}`;
    }
    // Check FAQs
    for (const faq of knowledgeBase.faqs) {
      if (faq.keywords.some(keyword => lowerQuestion.includes(keyword))) {
        return faq.answer;
      }
    }
    // Check services
    for (const service of knowledgeBase.services) {
      if (service.keywords.some(keyword => lowerQuestion.includes(keyword))) {
        return `${service.name}\n\n${service.description}\n\nCaracterísticas:\n${service.features.map(f => `• ${f}`).join('\n')}`;
      }
    }
    // Check for services list
    if (lowerQuestion.match(/\b(servicios|qué hacen|ofrecen)\b/)) {
      return `Ofrecemos los siguientes servicios:\n\n${knowledgeBase.services.map(s => `• ${s.name}: ${s.description}`).join('\n\n')}`;
    }
    // Check for projects
    if (lowerQuestion.match(/\b(proyectos|trabajos|portafolio)\b/)) {
      return `Algunos de nuestros proyectos destacados:\n\n${knowledgeBase.projects.map(p => `• ${p.name} (${p.category}): ${p.description}`).join('\n\n')}`;
    }
    return knowledgeBase.fallback;
  };
  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    // Simulate typing delay
    setTimeout(() => {
      const answer = findAnswer(inputValue);
      const orionMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: answer,
        sender: 'orion',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, orionMessage]);
      setIsTyping(false);
    }, 1000);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return <>
      {/* Floating Buttons */}
      <AnimatePresence data-id="element-102">
        {!isOpen && <div className="fixed bottom-6 right-6 z-50 flex gap-4" data-id="element-103">
            {/* WhatsApp Button */}
            <motion.a href="https://wa.me/50246933600" target="_blank" rel="noopener noreferrer" initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0,
          opacity: 0
        }} whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} className="w-16 h-16 bg-gradient-to-r from-green-600 to-green-500 rounded-full shadow-2xl shadow-green-500/50 flex items-center justify-center text-white hover:shadow-green-500/70 transition-shadow" data-id="element-104">
              <MessageCircleIcon className="h-8 w-8" data-id="element-105" />
            </motion.a>
            {/* ORION Button */}
            <motion.button initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0,
          opacity: 0
        }} whileHover={{
          scale: 1.1
        }} whileTap={{
          scale: 0.9
        }} onClick={() => setIsOpen(true)} className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full shadow-2xl shadow-purple-500/50 flex items-center justify-center text-white hover:shadow-purple-500/70 transition-shadow" data-id="element-106">
              <SparklesIcon className="h-8 w-8" data-id="element-107" />
            </motion.button>
          </div>}
      </AnimatePresence>
      {/* Chat Window */}
      <AnimatePresence data-id="element-108">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 100,
        scale: 0.8
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        height: isMinimized ? 'auto' : '600px'
      }} exit={{
        opacity: 0,
        y: 100,
        scale: 0.8
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed bottom-6 right-6 z-50 w-96 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border border-purple-500/30 overflow-hidden flex flex-col" data-id="element-109">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 flex items-center justify-between" data-id="element-110">
              <div className="flex items-center" data-id="element-111">
                <div className="relative" data-id="element-112">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center" data-id="element-113">
                    <SparklesIcon className="h-6 w-6 text-purple-600" data-id="element-114" />
                  </div>
                  <motion.div animate={{
                scale: [1, 1.2, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white" data-id="element-115" />
                </div>
                <div className="ml-3" data-id="element-116">
                  <h3 className="font-bold text-white" data-id="element-117">ORION</h3>
                  <p className="text-xs text-white/80" data-id="element-118">Asistente Virtual</p>
                </div>
              </div>
              <div className="flex gap-2" data-id="element-119">
                <button onClick={() => setIsMinimized(!isMinimized)} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors" data-id="element-120">
                  <MinimizeIcon className="h-5 w-5" data-id="element-121" />
                </button>
                <button onClick={() => setIsOpen(false)} className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors" data-id="element-122">
                  <XIcon className="h-5 w-5" data-id="element-123" />
                </button>
              </div>
            </div>
            {!isMinimized && <>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-124">
                  {messages.map(message => <motion.div key={message.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-125">
                      <div className={`max-w-[80%] rounded-2xl p-3 ${message.sender === 'user' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gray-700 text-gray-100'}`} data-id="element-126">
                        <p className="text-sm whitespace-pre-line" data-id="element-127">
                          {message.text}
                        </p>
                        <p className="text-xs mt-1 opacity-70" data-id="element-128">
                          {message.timestamp.toLocaleTimeString('es-ES', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                        </p>
                      </div>
                    </motion.div>)}
                  {isTyping && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-start" data-id="element-129">
                      <div className="bg-gray-700 rounded-2xl p-3 flex gap-1" data-id="element-130">
                        <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: 0
                }} className="w-2 h-2 bg-purple-400 rounded-full" data-id="element-131" />
                        <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: 0.2
                }} className="w-2 h-2 bg-purple-400 rounded-full" data-id="element-132" />
                        <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 0.6,
                  repeat: Infinity,
                  delay: 0.4
                }} className="w-2 h-2 bg-purple-400 rounded-full" data-id="element-133" />
                      </div>
                    </motion.div>}
                  <div ref={messagesEndRef} data-id="element-134" />
                </div>
                {/* Input */}
                <div className="p-4 bg-gray-800/50 border-t border-purple-500/20" data-id="element-135">
                  <div className="flex gap-2" data-id="element-136">
                    <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Escribe tu pregunta..." className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-400" data-id="element-137" />
                    <button onClick={handleSend} disabled={!inputValue.trim()} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-2 rounded-full hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-id="element-138">
                      <SendIcon className="h-5 w-5" data-id="element-139" />
                    </button>
                  </div>
                </div>
              </>}
          </motion.div>}
      </AnimatePresence>
    </>;
};
export default Orion;